/* eslint-disable react-refresh/only-export-components */
import { createContext, Dispatch, FC, Reducer, useReducer } from 'react';

import { getSideNavPreference } from 'app/api/cookie';
import { PropsWithOnlyChildren } from 'app/types';

import { getDeviceFromWidth } from './helpers';

// Local Typings
export type Device = 'desktop' | 'mobile' | 'tablet';
export type UiTheme = 'dark' | 'light' | 'system';
interface GeneralState {
  beamerMessageCount: number;
  currentDevice: Device;
  currentOpenFilterButton: null | string;
  isServiceWorkerUpdated: boolean;
  isSideNavOpen: boolean;
  uiTheme: UiTheme;
}

interface ResetAction {
  type: 'reset';
}
interface SetBeamerMessageCountAction {
  payload: {
    beamerMessageCount: number;
  };
  type: 'setBeamerMessageCount';
}
interface SetDeviceAction {
  payload: {
    currentDevice: Device;
  };
  type: 'setDevice';
}
interface SetOpenFilterButtonAction {
  payload: {
    currentOpenFilterButton: null | string;
  };
  type: 'setOpenFilterButton';
}
interface ToggleSideNavAction {
  type: 'toggleSideNav';
}
interface UpdateServiceWorkerAction {
  type: 'updateServiceWorker';
}
interface UpdateUiTheme {
  payload: {
    uiTheme: UiTheme;
  };
  type: 'updateUiTheme';
}
type GeneralContextAction =
  | ResetAction
  | SetBeamerMessageCountAction
  | SetDeviceAction
  | SetOpenFilterButtonAction
  | ToggleSideNavAction
  | UpdateServiceWorkerAction
  | UpdateUiTheme;
export type GeneralDispatch = Dispatch<GeneralContextAction>;

// Local Variables
function getInitialDevice(): Device {
  if (window) {
    return getDeviceFromWidth(window.innerWidth);
  }

  return 'desktop';
}
function getInitialSideNavOpenState(): boolean {
  const device = getInitialDevice();

  if (device !== 'desktop') {
    return false;
  }

  const sideNavPreference = getSideNavPreference();

  return sideNavPreference !== 'closed';
}

const initialState: GeneralState = {
  beamerMessageCount: 0,
  currentDevice: getInitialDevice(),
  currentOpenFilterButton: null,
  isServiceWorkerUpdated: false,
  isSideNavOpen: getInitialSideNavOpenState(),
  uiTheme: 'system',
};

export const GeneralStateContext = createContext(initialState);
export const GeneralDispatchContext = createContext<GeneralDispatch>(undefined as unknown as GeneralDispatch);

GeneralStateContext.displayName = 'GeneralState';
GeneralDispatchContext.displayName = 'GeneralDispatch';

const reducer: Reducer<GeneralState, GeneralContextAction> = (state, action) => {
  switch (action.type) {
    case 'setBeamerMessageCount':
      return {
        ...state,
        beamerMessageCount: action.payload.beamerMessageCount,
      };
    case 'setDevice': {
      const { currentDevice = 'desktop' } = action.payload;

      return {
        ...state,
        currentDevice,
      };
    }
    case 'setOpenFilterButton': {
      const { currentOpenFilterButton = null } = action.payload;

      return {
        ...state,
        currentOpenFilterButton,
      };
    }
    case 'reset':
      return { ...initialState };
    case 'toggleSideNav':
      return {
        ...state,
        isSideNavOpen: !state.isSideNavOpen,
      };
    case 'updateServiceWorker':
      return {
        ...state,
        isServiceWorkerUpdated: true,
      };
    case 'updateUiTheme':
      return {
        ...state,
        uiTheme: action.payload.uiTheme,
      };
    default:
      throw new Error();
  }
};

const GeneralContextProvider: FC<PropsWithOnlyChildren> = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <GeneralStateContext.Provider value={state}>
      <GeneralDispatchContext.Provider value={dispatch}>
        {children}
      </GeneralDispatchContext.Provider>
    </GeneralStateContext.Provider>
  );
};

export default GeneralContextProvider;
